import html2canvas from 'html2canvas';
import React from 'react';

const sanitizeFileName = (fileName: string) =>
  fileName
    .trim()
    .replace(/[^\w-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Captures a DOM element as a JPEG, expanding any scrollable regions so the full content is included.
 */
export const downloadFullElementScreenshot = async (
  elementRef: React.RefObject<HTMLElement>,
  fileName: string = 'download',
  scale: number = 2
) => {
  const original = elementRef.current;
  if (!original) return;

  await document.fonts.ready;

  const clone = original.cloneNode(true) as HTMLElement;

  // Remove overflow/size constraints so hidden content becomes visible in the clone.
  const styledElements = clone.querySelectorAll<HTMLElement>('[style]');
  styledElements.forEach((el) => {
    if (el.style.maxHeight) el.style.maxHeight = 'none';
    if (el.style.maxWidth) el.style.maxWidth = 'none';
    if (el.style.height) el.style.height = 'auto';
    if (el.style.width) el.style.width = el.style.width === '100%' ? '100%' : el.style.width;
    if (el.style.overflow) el.style.overflow = 'visible';
    if (el.style.overflowX) el.style.overflowX = 'visible';
    if (el.style.overflowY) el.style.overflowY = 'visible';
  });
  // Ensure the root clone can grow freely.
  clone.style.overflow = 'visible';
  clone.style.maxHeight = 'none';
  clone.style.height = 'auto';
  clone.style.maxWidth = 'none';
  clone.style.width = 'auto';

  // Copy canvas content
  const originalCanvases = original.querySelectorAll('canvas');
  const cloneCanvases = clone.querySelectorAll('canvas');
  originalCanvases.forEach((originalCanvas, index) => {
    if (index < cloneCanvases.length) {
      const cloneCanvas = cloneCanvases[index];
      const context = cloneCanvas.getContext('2d');
      if (context) {
        cloneCanvas.width = originalCanvas.width;
        cloneCanvas.height = originalCanvas.height;
        context.drawImage(originalCanvas, 0, 0);
      }
    }
  });

  // Normalize icons
  clone.querySelectorAll('.fa-duotone').forEach((icon) => {
    icon.classList.remove('fa-duotone');
    icon.classList.add('fa-solid');
  });

  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '0';
  wrapper.style.left = '-9999px';
  wrapper.style.opacity = '1';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.backgroundColor = '#ffffff';
  wrapper.style.overflow = 'visible';
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  // Explicitly expand Lomavis list containers so their full height is captured.
  clone.querySelectorAll<HTMLElement>('.lomavis-list1').forEach((el) => {
    el.style.maxHeight = 'none';
    el.style.height = 'auto';
    el.style.overflow = 'visible';
    el.style.overflowY = 'visible';
  });

  // Wait a frame so layout can recalc with the clone attached.
  await new Promise((resolve) => requestAnimationFrame(resolve));

  // Measure after constraints have been lifted so scrollable children (like lists) contribute.
  const width = Math.max(
    clone.scrollWidth,
    clone.getBoundingClientRect().width,
    original.scrollWidth,
    original.getBoundingClientRect().width
  );
  const height = Math.max(
    clone.scrollHeight,
    clone.getBoundingClientRect().height,
    original.scrollHeight,
    original.getBoundingClientRect().height
  );

  clone.style.width = `${width}px`;
  clone.style.minWidth = `${width}px`;
  clone.style.height = `${height}px`;
  clone.style.minHeight = `${height}px`;

  wrapper.style.width = `${width}px`;
  wrapper.style.height = `${height}px`;
  wrapper.style.minWidth = `${width}px`;
  wrapper.style.minHeight = `${height}px`;

  try {
    await new Promise((resolve) => setTimeout(resolve, 400));

    const canvas = await html2canvas(clone, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width,
      height,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height,
      ignoreElements: (element) => element.hasAttribute('data-html2canvas-ignore')
    });

    const dataURL = canvas.toDataURL('image/jpeg', 1.0);
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `${sanitizeFileName(fileName)}.jpeg`;
    link.click();
  } finally {
    document.body.removeChild(wrapper);
  }
};
